import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Download, Filter, X } from 'lucide-react';
import { adsApiService } from '../services/adsApiService';
import { AdCampaign } from '../types/ads'; // Import AdCampaign type

interface AnalyticsViewProps {
  onViewChange: (view: string) => void;
}

const AnalyticsView: React.FC<AnalyticsViewProps> = ({ onViewChange }) => {
  const [dateRange, setDateRange] = useState('30d');
  const [analytics, setAnalytics] = useState<any>(null);
  const [campaigns, setCampaigns] = useState<AdCampaign[]>([]); // State to store campaigns
  const [campaignMetrics, setCampaignMetrics] = useState<any[]>([]); // State to store aggregated metrics per campaign
  const [loading, setLoading] = useState(true);
  const [showCampaignDetailModal, setShowCampaignDetailModal] = useState(false);
  const [selectedCampaignForDetail, setSelectedCampaignForDetail] = useState<AdCampaign | null>(null);
  const [detailedCampaignAnalytics, setDetailedCampaignAnalytics] = useState<any>(null);
  const [detailedAnalyticsLoading, setDetailedAnalyticsLoading] = useState(false);

  useEffect(() => {
    loadData(); // Load both analytics and campaigns
  }, [dateRange]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - getDaysFromRange(dateRange) * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      const analyticsData = await adsApiService.getAnalytics({
        start_date: startDate,
        end_date: endDate
      });
      
      const campaignsData = await adsApiService.getCampaigns(); // Fetch all campaigns
      
      setAnalytics(analyticsData);
      setCampaigns(campaignsData);
      console.log('Existing campaigns in AnalyticsView:', campaignsData); // Log campaigns to console

      // Aggregate metrics per campaign
      const aggregatedCampaignMetrics: { [key: string]: any } = {};
      (analyticsData.metrics || []).forEach((metric: any) => {
        const campaignId = metric.campaign_id || metric?.ads?.ad_sets?.campaign_id;
        if (!campaignId) return;
        if (!aggregatedCampaignMetrics[campaignId]) {
          aggregatedCampaignMetrics[campaignId] = {
            impressions: 0,
            clicks: 0,
            spend: 0,
            campaign_id: campaignId,
          };
        }
        aggregatedCampaignMetrics[campaignId].impressions += (metric.impressions || 0);
        aggregatedCampaignMetrics[campaignId].clicks += (metric.clicks || 0);
        aggregatedCampaignMetrics[campaignId].spend += (metric.spend || 0);
      });

      const finalCampaignMetrics = campaignsData.map(campaign => {
        const metrics = aggregatedCampaignMetrics[campaign.id] || { impressions: 0, clicks: 0, spend: 0 };
        const ctr = metrics.impressions > 0 ? (metrics.clicks / metrics.impressions) * 100 : 0;
        const cpc = metrics.clicks > 0 ? metrics.spend / metrics.clicks : 0;
        return {
          ...campaign,
          total_impressions: metrics.impressions,
          total_clicks: metrics.clicks,
          total_spend: metrics.spend,
          average_ctr: ctr,
          average_cpc: cpc,
        };
      });
      setCampaignMetrics(finalCampaignMetrics);

    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getDaysFromRange = (range: string) => {
    switch (range) {
      case '7d': return 7;
      case '30d': return 30;
      case '90d': return 90;
      default: return 30;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(2) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(2) + 'K';
    }
    return num.toString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      case 'archived': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };


  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-gray-200 h-32 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const metrics = analytics?.summary || {
    impressions: 0,
    clicks: 0,
    spend: 0,
    average_ctr: 0,
    average_cpc: 0
  };

  const handleViewCampaignDetail = async (campaign: AdCampaign) => {
    setSelectedCampaignForDetail(campaign);
    setDetailedAnalyticsLoading(true);
    setShowCampaignDetailModal(true);

    try {
      const analyticsData = await adsApiService.getAnalytics({
        campaign_id: campaign.id,
        start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Last 30 days for detail
        end_date: new Date().toISOString().split('T')[0]
      });
      setDetailedCampaignAnalytics(analyticsData);
    } catch (err) {
      console.error('Error loading detailed campaign analytics:', err);
      setDetailedCampaignAnalytics(null);
    } finally {
      setDetailedAnalyticsLoading(false);
    }
  };

  return (
    <div className="p-4 lg:p-6 overflow-x-hidden min-w-0">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6 lg:mb-8 gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">Analíticas y Reportes</h1>
          <p className="text-gray-600 text-sm lg:text-base">Analiza el rendimiento de tus campañas publicitarias</p>
        </div>
        
        <div className="flex items-center gap-4">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-3 lg:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm lg:text-base"
          >
            <option value="7d">Últimos 7 días</option>
            <option value="30d">Últimos 30 días</option>
            <option value="90d">Últimos 90 días</option>
            <option value="custom">Rango personalizado</option>
          </select>
          
          <button className="flex items-center gap-2 px-3 lg:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm lg:text-base whitespace-nowrap">
            <Download size={18} />
            <span className="hidden lg:inline">Exportar</span>
          </button>
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6 mb-6 lg:mb-8">
        <div className="bg-white rounded-xl border border-gray-200 p-4 lg:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs lg:text-sm font-medium text-gray-600">Impresiones</h3>
            <TrendingUp className="text-green-500" size={16} />
          </div>
          <p className="text-xl lg:text-3xl font-bold text-gray-900">{formatNumber(metrics.impressions)}</p>
          <p className="text-xs lg:text-sm text-gray-500 mt-2">Últimos {getDaysFromRange(dateRange)} días</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-600">Clics</h3>
            <TrendingUp className="text-blue-500" size={16} />
          </div>
          <p className="text-3xl font-bold text-gray-900">{formatNumber(metrics.clicks)}</p>
          <p className="text-sm text-gray-500 mt-2">CTR: {metrics.average_ctr.toFixed(2)}%</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-600">Gasto Total</h3>
            <TrendingUp className="text-orange-500" size={16} />
          </div>
          <p className="text-3xl font-bold text-gray-900">{formatCurrency(metrics.spend)}</p>
          <p className="text-sm text-gray-500 mt-2">CPC: {formatCurrency(metrics.average_cpc)}</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-600">Alcance</h3>
            <TrendingUp className="text-purple-500" size={16} />
          </div>
          <p className="text-3xl font-bold text-gray-900">{formatNumber(metrics.reach || Math.floor(metrics.impressions * 0.7))}</p>
          <p className="text-sm text-gray-500 mt-2">Usuarios únicos</p>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6 mb-6 lg:mb-8">
        <div className="bg-white rounded-xl border border-gray-200 p-4 lg:p-6">
          <h3 className="text-xs lg:text-sm font-medium text-gray-600 mb-2">CTR (Click-Through Rate)</h3>
          <p className="text-lg lg:text-2xl font-bold text-gray-900">{metrics.average_ctr.toFixed(2)}%</p>
          <div className="mt-4 bg-gray-200 rounded-full h-2">
            <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${Math.min(metrics.average_ctr * 10, 100)}%` }}></div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-600 mb-2">CPC (Cost Per Click)</h3>
          <p className="text-2xl font-bold text-gray-900">{formatCurrency(metrics.average_cpc)}</p>
          <div className="mt-4 bg-gray-200 rounded-full h-2">
            <div className="bg-green-500 h-2 rounded-full" style={{ width: '60%' }}></div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-600 mb-2">CPM (Cost Per Mille)</h3>
          <p className="text-2xl font-bold text-gray-900">{formatCurrency(metrics.impressions > 0 ? (metrics.spend / metrics.impressions) * 1000 : 0)}</p>
          <div className="mt-4 bg-gray-200 rounded-full h-2">
            <div className="bg-purple-500 h-2 rounded-full" style={{ width: '45%' }}></div>
          </div>
        </div>

        {analytics?.daily_metrics?.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-sm font-medium text-gray-600 mb-2">Días Activos</h3>
            <p className="text-2xl font-bold text-gray-900">{analytics.daily_metrics.length}</p>
            <div className="mt-4 bg-gray-200 rounded-full h-2">
              <div className="bg-orange-500 h-2 rounded-full" style={{ width: `${(analytics.daily_metrics.length / getDaysFromRange(dateRange)) * 100}%` }}></div>
            </div>
          </div>
        )}
      </div>

      {/* Charts Section */}
      {analytics?.daily_metrics && analytics.daily_metrics.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8 mb-6 lg:mb-8">
          {/* Performance Chart */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 lg:p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-base lg:text-lg font-semibold text-gray-900">Rendimiento por Día</h3>
              <button className="p-2 text-gray-500 hover:text-gray-700 rounded-lg">
                <Filter size={16} />
              </button>
            </div>
            
            <div className="h-48 lg:h-64 flex items-end justify-center space-x-1 overflow-x-auto">
              {analytics.daily_metrics.slice(-14).map((metric: any, i: number) => {
                const maxSpend = Math.max(...analytics.daily_metrics.map((m: any) => m.spend));
                const height = maxSpend > 0 ? Math.max(20, (metric.spend / maxSpend) * 200) : 20;
                
                return (
                  <div key={i} className="flex flex-col items-center">
                    <div 
                      className="w-3 bg-blue-500 rounded-t hover:bg-blue-600 transition-colors cursor-pointer"
                      style={{ height: `${height}px` }}
                      title={`${formatCurrency(metric.spend)} - ${new Date(metric.date).toLocaleDateString('es-ES')}`}
                    ></div>
                    <span className="text-xs text-gray-500 mt-2 transform rotate-45 origin-left whitespace-nowrap">
                      {new Date(metric.date).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })}
                    </span>
                  </div>
                );
              })}
            </div>
            
            {analytics.daily_metrics.length === 0 && (
              <div className="h-64 flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <BarChart3 size={48} className="mx-auto mb-4 text-gray-300" />
                  <p>No hay datos de rendimiento disponibles</p>
                  <p className="text-sm">Crea campañas para ver métricas</p>
                </div>
              </div>
            )}
          </div>

          {/* Real Campaign Metrics */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 lg:p-6">
            <h3 className="text-base lg:text-lg font-semibold text-gray-900 mb-6">Resumen de Métricas</h3>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center p-2 lg:p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600 text-sm lg:text-base">Total Impresiones:</span>
                <span className="font-bold text-gray-900 text-sm lg:text-base">{formatNumber(metrics.impressions)}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">Total Clics:</span>
                <span className="font-bold text-gray-900">{formatNumber(metrics.clicks)}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">CTR Promedio:</span>
                <span className="font-bold text-gray-900">{metrics.average_ctr.toFixed(2)}%</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">CPC Promedio:</span>
                <span className="font-bold text-gray-900">{formatCurrency(metrics.average_cpc)}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg border border-green-200">
                <span className="text-green-700 font-medium">Total Invertido:</span>
                <span className="font-bold text-green-900 text-lg">{formatCurrency(metrics.spend)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Campaign Performance Table */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base lg:text-lg font-semibold text-gray-900">Rendimiento por Campaña</h3>
        <button 
          data-tour="analytics-create-campaign"
          onClick={() => onViewChange('campaigns')}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm lg:text-base whitespace-nowrap"
        >
          Crear Nueva Campaña
        </button>
      </div>
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6 lg:mb-8">
        <div className="px-4 lg:px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-xs lg:text-sm text-gray-500">
              {campaigns.length || 0} campañas encontradas
            </span>
          </div>
        </div>
        
        {campaignMetrics.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-3 lg:px-6 font-medium text-gray-600 text-xs lg:text-sm">Campaña</th>
                  <th className="text-left py-3 px-3 lg:px-6 font-medium text-gray-600 text-xs lg:text-sm">Impresiones</th>
                  <th className="text-left py-3 px-3 lg:px-6 font-medium text-gray-600 text-xs lg:text-sm">Clics</th>
                  <th className="text-left py-3 px-3 lg:px-6 font-medium text-gray-600 text-xs lg:text-sm">Gasto</th>
                  <th className="text-left py-3 px-3 lg:px-6 font-medium text-gray-600 text-xs lg:text-sm">CTR</th>
                  <th className="text-left py-3 px-3 lg:px-6 font-medium text-gray-600 text-xs lg:text-sm">CPC</th>
                  <th className="text-left py-3 px-3 lg:px-6 font-medium text-gray-600 text-xs lg:text-sm">Estado</th>
                  <th className="text-left py-3 px-3 lg:px-6 font-medium text-gray-600 text-xs lg:text-sm">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {campaignMetrics
                  .filter(campaign => campaign.status === 'active') // Filter for active campaigns
                  .filter(campaign => {
                    const campaignDate = new Date(campaign.created_at);
                    const endDate = new Date();
                    const startDate = new Date(Date.now() - getDaysFromRange(dateRange) * 24 * 60 * 60 * 1000);
                    return campaignDate >= startDate && campaignDate <= endDate;
                  })
                  .map((campaign: any, index: number) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="py-4 px-3 lg:px-6 font-medium text-gray-900 text-xs lg:text-sm">
                        {campaign.name || 'Campaña sin nombre'}
                      </td>
                      <td className="py-4 px-3 lg:px-6 text-gray-700 text-xs lg:text-sm">{formatNumber(campaign.total_impressions || 0)}</td>
                      <td className="py-4 px-3 lg:px-6 text-gray-700 text-xs lg:text-sm">{formatNumber(campaign.total_clicks || 0)}</td>
                      <td className="py-4 px-3 lg:px-6 text-gray-700 text-xs lg:text-sm">{formatCurrency(campaign.total_spend || 0)}</td>
                      <td className="py-4 px-3 lg:px-6 text-gray-700 text-xs lg:text-sm">{campaign.average_ctr.toFixed(2)}%</td>
                      <td className="py-4 px-3 lg:px-6 text-gray-700 text-xs lg:text-sm">{formatCurrency(campaign.average_cpc)}</td>
                      <td className="py-4 px-3 lg:px-6 text-gray-700 text-xs lg:text-sm">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(campaign.status)}`}>
                          {campaign.status === 'active' ? 'Activa' : 
                           campaign.status === 'paused' ? 'Pausada' : 'Archivada'}
                        </span>
                      </td>
                      <td className="py-4 px-3 lg:px-6 text-gray-700 text-xs lg:text-sm">
                        <button
                          onClick={() => handleViewCampaignDetail(campaign)}
                          className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-xs"
                        >
                          Ver Detalles
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center">
            <BarChart3 size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay campañas disponibles</h3>
            <p className="text-gray-500 mb-6">Crea campañas para verlas aquí</p>
          </div>
        )}
      </div>

      {/* Campaign Detail Modal */}
      {showCampaignDetailModal && selectedCampaignForDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  Detalles de Campaña: {selectedCampaignForDetail.name}
                </h2>
                <button
                  onClick={() => setShowCampaignDetailModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X size={20} />
                </button>
              </div>

              {detailedAnalyticsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              ) : detailedCampaignAnalytics ? (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-blue-900">
                        {formatNumber(detailedCampaignAnalytics.summary.impressions)}
                      </div>
                      <div className="text-sm text-blue-700">Impresiones</div>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-green-900">
                        {formatNumber(detailedCampaignAnalytics.summary.clicks)}
                      </div>
                      <div className="text-sm text-green-700">Clics</div>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-purple-900">
                        {detailedCampaignAnalytics.summary.average_ctr.toFixed(2)}%
                      </div>
                      <div className="text-sm text-purple-700">CTR</div>
                    </div>
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-orange-900">
                        {formatCurrency(detailedCampaignAnalytics.summary.spend)}
                      </div>
                      <div className="text-sm text-orange-700">Gastado</div>
                    </div>
                  </div>

                  {/* Performance Table */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-4">Rendimiento por día</h3>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {detailedCampaignAnalytics.metrics.map((metric: any, index: number) => (
                        <div key={index} className="flex justify-between items-center p-3 bg-white rounded-lg">
                          <span className="text-sm text-gray-600">
                            {new Date(metric.date).toLocaleDateString('es-ES')}
                          </span>
                          <div className="flex gap-4 text-sm">
                            <span className="text-blue-600">{metric.impressions || 0} imp.</span>
                            <span className="text-green-600">{metric.clicks || 0} clics</span>
                            <span className="text-orange-600">{formatCurrency(metric.spend || 0)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <BarChart3 size={48} className="mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No hay datos disponibles</h3>
                  <p className="text-gray-500">Esta campaña aún no tiene métricas registradas</p>
                </div>
              )}

              <div className="flex justify-end pt-6 mt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowCampaignDetailModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsView;
